import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, User, LogOut, Calendar, Settings } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/bikes", label: "Browse" },
  { to: "/cities", label: "Cities" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/support", label: "Support" },
];

export const Navbar = ({ variant = "light" }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isDark = variant === "dark";

  return (
    <header
      className={`sticky top-0 z-50 w-full ${isDark ? "bg-[#0A0B10]/70 border-b border-white/5" : "bg-white/80 border-b border-black/5"} backdrop-blur-xl`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo dark={isDark} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === l.to
                  ? (isDark ? "bg-white/10 text-white" : "bg-black/5 text-ink")
                  : (isDark ? "text-white/70 hover:text-white" : "text-gray-600 hover:text-ink")
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className={`flex items-center gap-2 px-3 py-2 rounded-full ${isDark ? "bg-white/5 hover:bg-white/10 text-white" : "bg-black/5 hover:bg-black/10"}`}>
                  <div className="w-7 h-7 rounded-full bg-brand-gradient flex items-center justify-center text-white text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm font-medium pr-1">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}>
                  <Calendar className="w-4 h-4 mr-2" /> My Bookings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/dashboard?tab=profile")}>
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
                    <Settings className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className={`text-sm font-semibold px-4 py-2 rounded-full ${isDark ? "text-white hover:bg-white/10" : "text-ink hover:bg-black/5"}`}>
                Log in
              </Link>
              <Link to="/signup" data-testid="nav-signup-btn" className="btn-primary text-sm !py-2.5 !px-5">
                Sign up
              </Link>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className={`md:hidden p-2 rounded-lg ${isDark ? "text-white" : "text-ink"}`}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className={`md:hidden border-t ${isDark ? "border-white/5 bg-[#0A0B10]" : "border-black/5 bg-white"} px-5 py-4`}>
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-white/80 hover:bg-white/5" : "text-gray-700 hover:bg-black/5"}`}>
                {l.label}
              </Link>
            ))}
            <div className="h-px bg-black/5 my-2" />
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium">My Bookings</Link>
                <button onClick={async () => { await logout(); nav("/"); setOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium text-red-500">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium">Log in</Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary justify-center !text-sm">Sign up</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
